import { defineStore } from 'pinia';
import { hydrated } from '@/storePinia';
import axios from 'axios';

import {
  IFavouriteGroups,
  IFavouriteMessages,
  IFavouriteRoutes,
  ILikes,
  ILikesIDs,
} from '@/storePinia/stores/favourite/IFavouriteStore';
import {
  IFavouriteProduct,
  IFavouriteProductSize,
} from '@/storePinia/stores/favourite/IFavouriteProduct';

import useLoader from '@use/useLoader/useLoader';

const loader = useLoader();

interface IFavourite {
  baseGroupID: string | null;
  count: number;
  printCount: string;
  groups: IFavouriteGroups;
  likes: ILikes;
  likesIDs: ILikesIDs;
  messages: IFavouriteMessages;
  /** hydrated from HTML (blocks/wish-list.php) on every page of the main template */
  routes: IFavouriteRoutes;
  updatedLikes?: {
    [name: string]: number;
  };
}

interface ICreateNewGroupPayload {
  name: string;
  id: number;
  changeBase: 'N' | 'Y';
}

interface IAddLikeToBaseGroupPayload {
  id: number;
}
interface IRemovePayload {
  id: number;
  groupId: number;
}
interface IRemoveGroupPayload {
  groupId: number;
}

interface IChangeSKUPayload {
  product: IFavouriteProduct;
  size: IFavouriteProductSize;
  groupId?: string;
}

type FavouriteGetters = {
  inFavourite: (state: IFavourite) => (payload: string | undefined) => boolean;
};

type FavouriteActions = {
  createNewGroup(payload: ICreateNewGroupPayload | FormData): Promise<void>;
  addLikeToBaseGroup(payload: IAddLikeToBaseGroupPayload | FormData): Promise<void>;
  remove(payload: IRemovePayload): Promise<void>;
  removeGroup(payload: IRemoveGroupPayload): Promise<void>;
  changeGroupName(payload: FormData): Promise<void>;
  moveProductsToGroup(payload: FormData): Promise<void>;
  changeSKU(payload: IChangeSKUPayload): Promise<void>;
};

export const useFavouriteStore = defineStore<
  'favourite',
  IFavourite,
  FavouriteGetters,
  FavouriteActions
>('favourite', {
  state: (): IFavourite => ({
    baseGroupID: '',
    count: 0,
    printCount: '',
    groups: [],
    likes: [],
    likesIDs: [],
    messages: hydrated<IFavouriteMessages>(),
    routes: hydrated<IFavouriteRoutes>(),
    updatedLikes: {},
  }),
  getters: {
    inFavourite: (state) => (payload) =>
      !!state?.likesIDs.find((el) => el.toString() === payload?.toString()),
  },
  actions: {
    async createNewGroup(payload: ICreateNewGroupPayload | FormData): Promise<void> {
      try {
        if (this.routes?.addLikeGroup) {
          const { path } = this.routes.addLikeGroup;
          const { action } = this.routes.addLikeGroup['params-example-data'];

          interface ICreateNewGroupRequestData extends ICreateNewGroupPayload {
            action: string;
          }

          let requestData: ICreateNewGroupRequestData | FormData;

          if (payload instanceof FormData) {
            payload.set('action', action);
            requestData = payload;
          } else {
            requestData = { ...payload, action };
          }

          const response = await axios.post(path, requestData);

          if (response.status !== 200) {
            throw new Error(`Bad response: ${response.status}`);
          }
          this.$patch(response.data as Partial<IFavourite>);
        }
      } catch (err) {
        console.error(err);
      }
    },
    async addLikeToBaseGroup(payload: IAddLikeToBaseGroupPayload | FormData) {
      try {
        if (this.routes?.addLikeToBaseGroup) {
          const {
            path,
            'params-example-data': { action },
          } = this.routes.addLikeToBaseGroup;

          interface IRequestData extends IAddLikeToBaseGroupPayload {
            action: string;
          }

          let requestData: IRequestData | FormData;
          // console.log(this.routes?.addLikeToBaseGroup);
          if (payload instanceof FormData) {
            payload.set('action', action);
            requestData = payload;
          } else {
            requestData = {
              ...payload,
              action,
            };
          }

          const response = await axios.post(path, requestData);

          if (response.status === 200) {
            this.$patch(response.data as Partial<IFavourite>);
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    async remove(payload: IRemovePayload) {
      try {
        if (this.routes?.deleteLike) {
          const {
            path,
            'params-example-data': { action },
          } = this.routes.deleteLike;

          const response = await axios.post(path, {
            action,
            ...payload,
          });
          if (response.status === 200) {
            this.$patch(response.data as Partial<IFavourite>);
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    async removeGroup(payload: IRemoveGroupPayload) {
      try {
        if (this.routes?.deleteLikeGroup) {
          const {
            path,
            'params-example-data': { action },
          } = this.routes.deleteLikeGroup;

          const response = await axios.post(path, {
            action,
            ...payload,
          });

          if (response.status === 200) {
            // console.log(response.data);
            this.$patch(response.data as Partial<IFavourite>);
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    async changeGroupName(payload: FormData) {
      try {
        if (this.routes?.renameLikeGroup) {
          const {
            path,
            'params-example-data': { action },
          } = this.routes.renameLikeGroup;

          payload.set('action', action);
          // payload.set('groupId', +payload.get('groupId'));

          const response = await axios.post(path, payload);
          if (response.status === 200) {
            this.$patch(response.data as Partial<IFavourite>);
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    async moveProductsToGroup(payload: FormData) {
      try {
        if (this.routes?.moveLikesGroup) {
          const {
            path,
            'params-example-data': { action },
          } = this.routes.moveLikesGroup;

          payload.set('action', action);

          const response = await axios.post(path, payload);

          if (response.status === 200) {
            this.$patch(response.data as Partial<IFavourite>);
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    async changeSKU(payload: IChangeSKUPayload) {
      try {
        loader.start();
        const { product, size } = payload;

        // remove old product
        if (this.routes?.deleteLike) {
          await axios.post(this.routes.deleteLike.path, {
            action: this.routes.deleteLike['params-example-data'].action,
            id: product.offer?.id || product.id,
            groupId: payload.groupId || product.groupId,
          });
        }

        // add new product
        if (this.routes?.addLikeGroup) {
          const requestParams: {
            action: string;
            id: number | string;
            groupId?: string;
          } = {
            action: this.routes.addLikeGroup['params-example-data'].action,
            id: size.id,
          };

          if (payload.groupId || product.groupId) {
            requestParams.groupId = payload.groupId || product.groupId;
          }

          const { data: responseData } = await axios.post<
            { action: string; id: number | string; groupId?: string },
            {
              data: IFavourite;
            }
          >(this.routes.addLikeGroup.path, requestParams);

          const newProduct = responseData.likes.find((el) => {
            const id = el.offer?.id || el.id;

            return id === size.id;
          });

          // update product in favourite store
          if (newProduct) {
            // in likes
            const likes = this.likes.map((el) => {
              if ((el.offer?.id || el.id) !== (product.offer?.id || product.id)) {
                return el;
              }

              return newProduct;
            });

            // in groups
            const groupIndex = this.groups.findIndex((el) => el.id === newProduct.groupId);

            if (groupIndex !== -1) {
              const groups = [...this.groups];

              groups[groupIndex] = {
                ...groups[groupIndex],
                items: groups[groupIndex].items.map((el) => {
                  if ((el.offer?.id || el.id) !== (product.offer?.id || product.id)) {
                    return el;
                  }

                  return newProduct;
                }),
              };

              this.$patch({
                groups,
                likes,
              });
            }
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        loader.stop();
      }
    },
  },
});
